import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractContentComponent,
  ResponsiveInput,
} from '@epicuro-next/elements/content-core';
import { PulseContentElement } from '@epicuro-next/elements/core';
import { openOverlayOutlet } from '@epicuro-next/state/router-store';
import { isEqual } from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import { Product, SearchQuery } from 'graphql-api/gql-api-interfaces';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';

import { PCardsService } from '../../service/pcards-service';
import { AsyncPipe } from '@angular/common';

const selector = 'pcn-pcards';

@PulseContentElement({ selector })
@Component({
  selector,
  templateUrl: './pcards.component.html',
  styleUrls: ['./pcards.component.scss'],
  imports: [AsyncPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PcardsComponent
  extends AbstractContentComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  //

  @ResponsiveInput()
  @Input()
  public query: string;

  @ResponsiveInput({ isNumber: true })
  @Input()
  public maxProducts = 10;

  @ResponsiveInput({ isBoolean: true })
  @Input()
  public rowsEnabled = false;

  public products$: Observable<readonly Product[]>;
  public query$ = new Subject<SearchQuery>();

  constructor(
    private pcardsService: PCardsService,
    private store: Store,
    cdr: ChangeDetectorRef,
    elm: ElementRef,
    zone: NgZone,
  ) {
    super(cdr, elm, zone);
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { maxProducts, query } = changes;
    if (
      (maxProducts && !maxProducts.firstChange) ||
      (query && !query.firstChange)
    ) {
      this.updateQuery();
    }
  }

  public ngOnInit() {
    super.ngOnInit();
    this.buildProductsStream();
  }

  public ngAfterViewInit() {
    this.updateQuery();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
  }

  public clickedProductCard(product: Product) {
    const overlayPath = [
      'cs',
      product.brandId,
      product.parent?.productId ?? product.productId,
      product.productId,
    ];

    this.store.dispatch(openOverlayOutlet({ overlayPath }));
  }

  private buildProductsStream() {
    this.products$ = this.query$.pipe(
      distinctUntilChanged(isEqual),
      switchMap((query) => this.pcardsService.getProducts(query)),
    );

    this.cdr.markForCheck();
  }

  private updateQuery() {
    this.query$.next(buildQueryObject(this.query, this.maxProducts));
  }
}

function buildQueryObject(str: string, num: number): SearchQuery {
  const searchParams = new URLSearchParams(str);
  const query: { [key: string]: string | number } = {
    num,
  };
  searchParams.forEach((value, key) => {
    query[key] = value;
  });
  return query as SearchQuery;
}
